import React from "react";
import { Input } from "antd";
import { LockOutlined, CheckOutlined } from "@ant-design/icons";
import { userPutChangePassword } from "@apis";
import { useToaster } from "@components";
import { AccountModalShell } from "./account-modal-shell";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const hasMinLength = (v: string) => v.length >= 8;
const hasLettersAndNumbers = (v: string) => /[a-zA-Z]/.test(v) && /[0-9]/.test(v);
const hasSymbol = (v: string) => /[^a-zA-Z0-9]/.test(v);

/**
 * Modal para que el usuario cambie su contraseña voluntariamente
 * desde el menú del avatar. Para el cambio obligatorio de primer ingreso
 * ver ForceChangePasswordModal.
 */
export const ChangePasswordModal = ({
  open,
  onClose,
}: ChangePasswordModalProps) => {
  const toaster = useToaster();
  const [backPass, setBackPass] = React.useState("");
  const [newPass, setNewPass] = React.useState("");
  const [confPass, setConfPass] = React.useState("");
  const [processing, setProcessing] = React.useState(false);

  const reset = () => {
    setBackPass("");
    setNewPass("");
    setConfPass("");
    setProcessing(false);
  };

  const handleClose = () => {
    if (processing) return;
    reset();
    onClose();
  };

  const rules = [
    { ok: hasMinLength(newPass), label: "Mínimo 8 caracteres" },
    { ok: hasLettersAndNumbers(newPass), label: "Letras y números" },
    { ok: hasSymbol(newPass), label: "Un símbolo (recomendado)" },
  ];

  const canSubmit =
    !!backPass.trim() &&
    hasMinLength(newPass) &&
    hasLettersAndNumbers(newPass) &&
    confPass === newPass &&
    !processing;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (newPass === backPass) {
      toaster.error({
        title: "Error",
        message: "La nueva contraseña debe ser distinta a la actual",
        duration: 4000,
        placement: "topRight",
      });
      return;
    }
    try {
      setProcessing(true);
      const { data } = await userPutChangePassword({
        backPass: backPass.trim(),
        newPass: newPass.trim(),
      });
      toaster.success({
        title: "Listo",
        message: data ?? "Contraseña actualizada",
        duration: 4000,
        placement: "topRight",
      });
      reset();
      onClose();
    } catch (err: any) {
      toaster.error({
        title: "Error",
        message: err?.message ?? "No se pudo cambiar la contraseña",
        duration: 5000,
        placement: "topRight",
      });
      setProcessing(false);
    }
  };

  return (
    <AccountModalShell
      open={open}
      onClose={handleClose}
      icon={<LockOutlined />}
      title="Cambiar contraseña"
      subtitle="Protege el acceso a tu cuenta"
      footer={
        <>
          <button
            type="button"
            className="acct-btn acct-btn--ghost"
            onClick={handleClose}
            disabled={processing}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="acct-btn acct-btn--primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {processing ? "Guardando…" : "Guardar cambios"}
          </button>
        </>
      }
    >
      <div className="acct-field">
        <label className="acct-field__label">Contraseña actual</label>
        <Input.Password
          size="large"
          placeholder="Tu contraseña vigente"
          value={backPass}
          onChange={(e) => setBackPass(e.target.value)}
          disabled={processing}
        />
      </div>

      <div className="acct-field">
        <label className="acct-field__label">Nueva contraseña</label>
        <Input.Password
          size="large"
          placeholder="Crea una contraseña segura"
          value={newPass}
          onChange={(e) => setNewPass(e.target.value)}
          disabled={processing}
        />
        <ul className="acct-rules">
          {rules.map((r) => (
            <li key={r.label} className={r.ok ? "is-ok" : ""}>
              <span className="acct-rules__dot">
                <CheckOutlined />
              </span>
              {r.label}
            </li>
          ))}
        </ul>
      </div>

      <div className="acct-field">
        <label className="acct-field__label">Confirmar nueva contraseña</label>
        <Input.Password
          size="large"
          placeholder="Repite la nueva contraseña"
          value={confPass}
          onChange={(e) => setConfPass(e.target.value)}
          onPressEnter={handleSubmit}
          disabled={processing}
          status={confPass && confPass !== newPass ? "error" : undefined}
        />
      </div>
    </AccountModalShell>
  );
};
